import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { User } from 'firebase/auth';
import PageHeader from './ui/PageHeader';
import '../styles/homeQuietLounge.css';
import VibeScoreCard from './VibeScoreCard';
import ChakraBarDemo from './ChakraBarDemo';
import ScrollIndicator from './ui/ScrollIndicator';
import TimeTrackingDashboard from './timeTracking/TimeTrackingDashboard';
import CheckOutReflectionModal from './timeTracking/CheckOutReflectionModal';
import { ProfileWidget, CapabilityOverview, getLatestCompleteAssessment, getLatestAssessment } from './Doelstellingen/Milestone_organizer/capabilities';
import { CompleteAssessment } from './Doelstellingen/Milestone_organizer/capabilities/types';
import { AnalyticsIcon, TimeIcon, DocumentIcon, CommunityIcon, ChatIcon, ProfileIcon, TrendIcon, GoalIcon, MindfulIcon, ThinkIcon, MailIcon } from './icons';
import { ThumbsUp, MessageCircle, RefreshCw } from 'lucide-react';
import type { IconWeight } from 'phosphor-react';

interface VibesPageProps {
  user: User | null;
  userProfile: any;
  mobileTabIndex?: number;
}

interface AnalyticsData {
  totalPosts: number;
  totalConnections: number;
  totalViews: number;
  engagementRate: number;
  weeklyActivity: Array<{
    day: string;
    posts: number;
    connections: number;
    views: number;
  }>;
  topContent: Array<{
    id: string;
    content: string;
    likes: number;
    comments: number;
    shares: number;
    timestamp: Date;
  }>;
  connectionGrowth: Array<{
    date: string;
    count: number;
  }>;
}

const VibesPage: React.FC<VibesPageProps> = ({ user, userProfile, mobileTabIndex = 0 }) => {
  // Movement-style: analytics sidebar state
  type AnalyticsKey = 'overview' | 'content' | 'connections' | 'engagement' | 'chakra-demo' | 'entrepreneur-profile' | 'mindtools' | 'mindtraining' | 'time-tracking';
  type MobileVibesFilter = 'all' | 'vibescore' | AnalyticsKey;
  const [activeAnalyticsKey, setActiveAnalyticsKey] = useState<AnalyticsKey>('overview');
  const [mobileVibesFilter, setMobileVibesFilter] = useState<MobileVibesFilter>('all');
  
  const analyticsList: Array<{ key: AnalyticsKey; title: string; subtitle: string; icon: React.ComponentType<{size?: string | number; weight?: IconWeight; className?: string}> }> = [
    { key: 'overview', title: 'Overzicht', subtitle: 'Algemene statistieken', icon: AnalyticsIcon },
    { key: 'time-tracking', title: 'Time & Value Tracking', subtitle: 'Tijd & waarde analyse', icon: TimeIcon },
    { key: 'content', title: 'Content Performance', subtitle: 'Posts en bereik', icon: DocumentIcon },
    { key: 'connections', title: 'Connectie Groei', subtitle: 'Netwerk ontwikkeling', icon: CommunityIcon },
    { key: 'engagement', title: 'Engagement', subtitle: 'Interactie en betrokkenheid', icon: ChatIcon },
    { key: 'chakra-demo', title: 'ChakraBar Demo', subtitle: 'Test VibeScore visualisatie', icon: TrendIcon },
    { key: 'entrepreneur-profile', title: 'Ondernemer Profiel', subtitle: 'DISC, Werkstijl & Skills', icon: ProfileIcon },
    // Hidden incomplete modules (not deleted, just hidden from navigation):
    // { key: 'mindtools', title: 'MindTools', subtitle: 'Mindfulness & wellness tools', icon: MindfulIcon },
    // { key: 'mindtraining', title: 'MindTraining', subtitle: 'Cognitive & mental training', icon: ThinkIcon },
  ];

  const [loading, setLoading] = useState(false);
  const [showVibeScoreAnalyzer, setShowVibeScoreAnalyzer] = useState(false);
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [profileAssessment, setProfileAssessment] = useState<CompleteAssessment | null>(null);
  const [activeGoalId, setActiveGoalId] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    totalPosts: 0,
    totalConnections: 0,
    totalViews: 0,
    engagementRate: 0,
    weeklyActivity: [],
    topContent: [],
    connectionGrowth: []
  });

  useEffect(() => {
    const mapped: MobileVibesFilter[] = ['all', 'content', 'connections', 'engagement', 'entrepreneur-profile'];
    const nextFilter = mapped[mobileTabIndex] || 'all';
    setMobileVibesFilter(nextFilter);
    if (nextFilter !== 'all' && nextFilter !== 'vibescore') {
      setActiveAnalyticsKey(nextFilter);
      setShowVibeScoreAnalyzer(false);
    }
  }, [mobileTabIndex]);

  // Load analytics data
  useEffect(() => {
    if (userProfile?.uid) {
      loadAnalyticsData();
    }
  }, [userProfile?.uid]);

  // Load profile assessment data
  useEffect(() => {
    const loadProfileAssessment = async () => {
      if (!userProfile?.uid) return;
      
      try {
        // Get active goal ID from Doelstellingen first
        // For now, we'll use the first goal we find
        const goalsQuery = query(
          collection(db, 'goals'),
          where('ownerId', '==', userProfile.uid),
          where('status', '==', 'active'),
          limit(1)
        );
        const goalsSnapshot = await getDocs(goalsQuery);
        if (!goalsSnapshot.empty) {
          const goalId = goalsSnapshot.docs[0].id;
          setActiveGoalId(goalId);
          
          // Try to load complete assessment (DISC + Werkstijl) for this goal
          const completeAssessment = await getLatestCompleteAssessment(userProfile.uid, goalId);
          setProfileAssessment(completeAssessment);
        }
      } catch (error) {
        console.error('Failed to load profile assessment:', error);
      }
    };

    loadProfileAssessment();
  }, [userProfile?.uid]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Load posts count
      const postsQuery = query(
        collection(db, 'communityPosts'),
        where('authorId', '==', userProfile.uid)
      );
      const postsSnapshot = await getDocs(postsQuery);
      const totalPosts = postsSnapshot.size;

      // Load connections count
      const connectionsQuery1 = query(
        collection(db, 'connections'),
        where('user1Id', '==', userProfile.uid)
      );
      const connectionsQuery2 = query(
        collection(db, 'connections'),
        where('user2Id', '==', userProfile.uid)
      );
      const [connectionsSnapshot1, connectionsSnapshot2] = await Promise.all([
        getDocs(connectionsQuery1),
        getDocs(connectionsQuery2)
      ]);
      const totalConnections = connectionsSnapshot1.size + connectionsSnapshot2.size;

      // Load top content (last 10 posts with engagement)
      const topPostsQuery = query(
        collection(db, 'communityPosts'),
        where('authorId', '==', userProfile.uid),
        orderBy('timestamp', 'desc'),
        limit(10)
      );
      const topPostsSnapshot = await getDocs(topPostsQuery);
      const topContent = topPostsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          content: data.content?.substring(0, 100) + '...' || '',
          likes: data.likes?.length || 0,
          comments: data.comments?.length || 0,
          shares: data.shares || 0,
          timestamp: data.timestamp?.toDate() || new Date()
        };
      });

      // Generate sample weekly activity data
      const weeklyActivity = [
        { day: 'Ma', posts: Math.floor(Math.random() * 5), connections: Math.floor(Math.random() * 3), views: Math.floor(Math.random() * 50) },
        { day: 'Di', posts: Math.floor(Math.random() * 5), connections: Math.floor(Math.random() * 3), views: Math.floor(Math.random() * 50) },
        { day: 'Wo', posts: Math.floor(Math.random() * 5), connections: Math.floor(Math.random() * 3), views: Math.floor(Math.random() * 50) },
        { day: 'Do', posts: Math.floor(Math.random() * 5), connections: Math.floor(Math.random() * 3), views: Math.floor(Math.random() * 50) },
        { day: 'Vr', posts: Math.floor(Math.random() * 5), connections: Math.floor(Math.random() * 3), views: Math.floor(Math.random() * 50) },
        { day: 'Za', posts: Math.floor(Math.random() * 5), connections: Math.floor(Math.random() * 3), views: Math.floor(Math.random() * 50) },
        { day: 'Zo', posts: Math.floor(Math.random() * 5), connections: Math.floor(Math.random() * 3), views: Math.floor(Math.random() * 50) }
      ];

      // Generate sample connection growth data
      const connectionGrowth = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        connectionGrowth.push({
          date: date.toLocaleDateString('nl-NL', { month: 'short', day: 'numeric' }),
          count: Math.floor(Math.random() * 10) + totalConnections - 5
        });
      }

      const totalViews = Math.floor(totalPosts * 25 + Math.random() * 100);
      const engagementRate = totalPosts > 0 ? ((topContent.reduce((sum, post) => sum + post.likes + post.comments, 0) / totalPosts) * 100) : 0;

      setAnalyticsData({
        totalPosts,
        totalConnections,
        totalViews,
        engagementRate,
        weeklyActivity,
        topContent,
        connectionGrowth
      });

    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Weekly Activity Chart */}
      <div className="bg-gray-50 rounded-lg border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Wekelijkse Activiteit</h3>
        <div className="space-y-3">
          {analyticsData.weeklyActivity.map((day, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 w-8">{day.day}</span>
              <div className="flex-1 mx-4">
                <div className="flex space-x-1">
                  <div className="flex-1 bg-blue-100 rounded-full h-2">
                    <div 
                      className="bg-blue-500 rounded-full h-2" 
                      style={{ width: `${(day.posts / 5) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex-1 bg-green-100 rounded-full h-2">
                    <div 
                      className="bg-green-500 rounded-full h-2" 
                      style={{ width: `${(day.connections / 3) * 100}%` }}
                    ></div>
                  </div>
                  <div className="flex-1 bg-purple-100 rounded-full h-2">
                    <div 
                      className="bg-purple-500 rounded-full h-2" 
                      style={{ width: `${(day.views / 50) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500 w-16 text-right">
                {day.posts}p {day.connections}c {day.views}v
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderContent = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Content</h3>
        <div className="space-y-4">
          {analyticsData.topContent.map((post, index) => (
            <div key={post.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 text-sm font-medium">#{index + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 mb-2">{post.content}</p>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><ThumbsUp size={12} /> {post.likes}</span>
                  <span className="flex items-center gap-1"><MessageCircle size={12} /> {post.comments}</span>
                  <span className="flex items-center gap-1"><RefreshCw size={12} /> {post.shares}</span>
                  <span>{post.timestamp.toLocaleDateString('nl-NL')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderConnections = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Connectie Groei</h3>
        <div className="space-y-3">
          {analyticsData.connectionGrowth.map((data, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm text-gray-700">{data.date}</span>
              <div className="flex-1 mx-4">
                <div className="bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 rounded-full h-2" 
                    style={{ width: `${(data.count / Math.max(...analyticsData.connectionGrowth.map(d => d.count))) * 100}%` }}
                  ></div>
                </div>
              </div>
              <span className="text-sm font-medium text-gray-900 w-12 text-right">{data.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderEngagement = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600 mb-2">{analyticsData.engagementRate.toFixed(1)}%</div>
            <div className="text-sm text-blue-700">Gemiddeld Engagement</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-3xl font-bold text-green-600 mb-2">
              {analyticsData.topContent.reduce((sum, post) => sum + post.likes + post.comments, 0)}
            </div>
            <div className="text-sm text-green-700">Totaal Interacties</div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render functions for new cards
  const renderMindTools = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="mb-4"><MindfulIcon size={48} className="text-gray-400 mx-auto" /></div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">MindTools</h3>
        <p className="text-gray-600">Mindfulness & wellness tools voor een Clean Mind</p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        {/* Mood Tracker */}
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Mood Tracker</h4>
          <p className="text-gray-600 mb-4">
            Track je dagelijkse stemming en krijg inzicht in je emotionele welzijn.
          </p>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
            Start Mood Check
          </button>
        </div>

        {/* Breathing Exercise */}
        <div className="bg-emerald-50 p-6 rounded-lg border border-emerald-200">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Breathing Exercise</h4>
          <p className="text-gray-600 mb-4">
            Kalmeer je geest met geleide ademhalingsoefeningen.
          </p>
          <button className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium">
            Start Oefening
          </button>
        </div>

        {/* Mindfulness Tips */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Mindfulness Tips</h4>
          <p className="text-gray-600 mb-4">
            Dagelijkse tips en technieken voor meer mindfulness.
          </p>
          <button className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium">
            Bekijk Tips
          </button>
        </div>

        {/* Stress Monitor */}
        <div className="bg-stone-50 p-6 rounded-lg border border-stone-200">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Stress Monitor</h4>
          <p className="text-gray-600 mb-4">
            Monitor je stress levels en krijg gepersonaliseerde adviezen.
          </p>
          <button className="bg-[#4F6660] text-white px-4 py-2 rounded-lg hover:bg-[#4F6660] transition-colors text-sm font-medium">
            Check Stress Level
          </button>
        </div>
      </div>

      <div className="mt-8 p-6 bg-stone-50 rounded-lg border border-stone-200">
        <div className="text-center">
          <h4 className="text-lg font-medium text-gray-700 mb-2">Upgrade naar Premium</h4>
          <p className="text-gray-600 mb-4">
            Ontgrendel mindfulness coaching, gepersonaliseerde affirmations en meer!
          </p>
          <button className="bg-[#4F6660] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#4F6660] transition-colors">
            Start 7 Dagen Gratis Trial
          </button>
        </div>
      </div>
    </div>
  );

  const renderMindTraining = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="mb-4"><ThinkIcon size={48} className="text-gray-400 mx-auto" /></div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">MindTraining</h3>
        <p className="text-gray-600">Cognitive & mental training voor ondernemers</p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        {/* Focus Training */}
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Focus Training</h4>
          <p className="text-gray-600 mb-4">
            Verbeter je concentratie en focus met specifieke oefeningen.
          </p>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
            Start Training
          </button>
        </div>

        {/* Memory Enhancement */}
        <div className="bg-stone-50 p-6 rounded-lg border border-stone-200">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Memory Enhancement</h4>
          <p className="text-gray-600 mb-4">
            Train je geheugen met ondernemers-specifieke oefeningen.
          </p>
          <button className="bg-[#4F6660] text-white px-4 py-2 rounded-lg hover:bg-[#4F6660] transition-colors text-sm font-medium">
            Start Training
          </button>
        </div>

        {/* Decision Making */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Decision Making</h4>
          <p className="text-gray-600 mb-4">
            Verbeter je besluitvorming met cognitieve training.
          </p>
          <button className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium">
            Start Training
          </button>
        </div>

        {/* Creative Thinking */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Creative Thinking</h4>
          <p className="text-gray-600 mb-4">
            Stimuleer je creativiteit met innovatieve oefeningen.
          </p>
          <button className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium">
            Start Training
          </button>
        </div>
      </div>

      <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
        <div className="text-center">
          <h4 className="text-lg font-medium text-gray-700 mb-2">Upgrade naar Premium</h4>
          <p className="text-gray-600 mb-4">
            Ontgrendel coaching, gepersonaliseerde training programma's en meer!
          </p>
          <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors">
            Start 7 Dagen Gratis Trial
          </button>
        </div>
      </div>
    </div>
  );

  const renderEntrepreneurProfile = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="mb-4"><ProfileIcon size={48} className="text-gray-400 mx-auto" /></div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Ondernemer Profiel</h3>
        <p className="text-gray-600">Je persoonlijkheid, werkstijl en skills overzicht</p>
      </div>

      {/* ProfileWidget - DISC + Werkstijl */}
      {profileAssessment && profileAssessment.assessmentLevel !== 'none' ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            Persoonlijkheid & Werkstijl
          </h4>
          <ProfileWidget assessment={profileAssessment} />
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-blue-700 mb-4">
            Je hebt nog geen persoonlijkheidsassessment voltooid.
          </p>
          <p className="text-sm text-blue-600 mb-4">
            Ga naar <strong>Doelstellingen</strong> en klik op <strong>"Capaciteiten checken"</strong> om je DISC en Werkstijl profiel te ontdekken.
          </p>
        </div>
      )}

      {/* CapabilityOverview - Skills per domein */}
      {activeGoalId && userProfile?.uid ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            Capaciteiten per Domein
          </h4>
          <CapabilityOverview 
            goalId={activeGoalId}
            ownerId={userProfile.uid}
          />
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-700 mb-4">
            Geen actief doel gevonden met capaciteiten assessment.
          </p>
          <p className="text-sm text-gray-600">
            Start een nieuw doel in <strong>Doelstellingen</strong> om je skills bij te houden.
          </p>
        </div>
      )}

      {/* Info Card */}
      <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
        <div className="text-center">
          <h4 className="text-lg font-medium text-gray-700 mb-2">Waarom deze data belangrijk is</h4>
          <p className="text-gray-600 text-sm mb-4">
            Je DISC profiel helpt je begrijpen hoe je communiceert en beslissingen neemt.
            Je Werkstijl toont hoe je het beste functioneert.
            Je Skills geven inzicht in waar je sterk bent en waar je kunt groeien.
          </p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-3 space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
            <div className="lg:col-span-9">
              <div className="h-96 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderAnalyticsPanel = (key: AnalyticsKey) => {
    if (key === 'overview') return renderOverview();
    if (key === 'time-tracking' && userProfile) {
      return (
        <TimeTrackingDashboard
          userId={userProfile.uid}
          userProfile={userProfile}
          onRequestCheckOut={() => setShowCheckOutModal(true)}
        />
      );
    }
    if (key === 'content') return renderContent();
    if (key === 'connections') return renderConnections();
    if (key === 'engagement') return renderEngagement();
    if (key === 'chakra-demo') return <ChakraBarDemo />;
    if (key === 'entrepreneur-profile') return renderEntrepreneurProfile();
    if (key === 'mindtools') return renderMindTools();
    if (key === 'mindtraining') return renderMindTraining();
    return renderOverview();
  };

  return (
    <div className="quiet-lounge min-h-full bg-[#F4F1EA] p-0 md:min-h-screen md:bg-[#F4F4F1] md:p-6">
      <div className="grid grid-cols-12 gap-0 md:gap-8 max-w-5xl mx-auto">
        {/* Left Sidebar - Individual Cards */}
        <div className="hidden lg:block lg:col-span-3">
          <div className="flex flex-col gap-3 px-4 pt-4 pb-24 md:gap-4 md:p-0">
            {/* VibeScore Small Card */}
            <div className="bg-white/85 rounded-2xl shadow-sm border border-[#E8E4DC] md:bg-white md:rounded-lg md:border-gray-200">
              <div className="p-4 cursor-pointer flex items-center justify-between" onClick={() => setShowVibeScoreAnalyzer(!showVibeScoreAnalyzer)}>
                <div className="flex items-center space-x-2">
                  <AnalyticsIcon size={18} weight="regular" className="text-[#7A9488]" />
              <div>
                    <h3 className="font-semibold text-[#2F3A36]">VibeScore Analyzer</h3>
                    <p className="text-sm text-[#7A7F78]">Analyseer je vibe</p>
                  </div>
                </div>
                <span className="text-[#8A8A84]">{showVibeScoreAnalyzer ? '▾' : '▸'}</span>
              </div>
            </div>

            {/* Analytics Navigation Card */}
            <div className="bg-white/85 rounded-2xl shadow-sm border border-[#E8E4DC] md:bg-white md:rounded-lg md:border-gray-200">
              <div className="p-4 border-b border-[#E8E4DC] cursor-pointer flex items-center justify-between" onClick={() => setAnalyticsExpanded(!analyticsExpanded)}>
                <div className="flex items-center space-x-2">
                  <AnalyticsIcon size={18} weight="regular" className="text-[#7A9488]" />
                  <div>
                    <h3 className="font-semibold text-[#2F3A36]">Analytics</h3>
                    <p className="text-sm text-[#7A7F78]">Community prestaties</p>
                  </div>
                </div>
                <span className="text-[#8A8A84]">{analyticsExpanded ? '▾' : '▸'}</span>
              </div>
              {analyticsExpanded && (
                <div className="p-3 space-y-2">
                  {analyticsList.map((analytics) => (
                    <button
                      key={analytics.key}
                      onClick={() => {
                        setActiveAnalyticsKey(analytics.key);
                        setShowVibeScoreAnalyzer(false); // Reset VibeScore Analyzer when switching
                      }}
                      className={`w-full text-left p-3 rounded-xl transition-colors ${
                        activeAnalyticsKey === analytics.key ? 'bg-[#F0F7F4] text-[#4F6660] border border-[#DDE7DF]' : 'hover:bg-[#F7F5EF]'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                          <analytics.icon 
                            size={18} 
                            weight="regular" 
                            className={activeAnalyticsKey === analytics.key ? 'text-[#4F6660]' : 'text-[#7A9488]'}
                          />
                          <div>
                            <h4 className={`font-medium text-sm ${
                              activeAnalyticsKey === analytics.key ? 'text-[#4F6660]' : 'text-[#2F3A36]'
                            }`}>{analytics.title}</h4>
                            <p className="text-xs text-[#7A7F78]">{analytics.subtitle}</p>
                          </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {!analyticsExpanded && (
                <div className="p-3 space-y-2">
                  {analyticsList.slice(0, 3).map((analytics) => (
                    <button
                      key={analytics.key}
                      onClick={() => {
                        setActiveAnalyticsKey(analytics.key);
                        setShowVibeScoreAnalyzer(false); // Reset VibeScore Analyzer when switching
                      }}
                      className={`w-full text-left p-3 rounded-xl transition-colors ${
                        activeAnalyticsKey === analytics.key ? 'bg-[#F0F7F4] text-[#4F6660] border border-[#DDE7DF]' : 'hover:bg-[#F7F5EF]'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                          <analytics.icon 
                            size={18} 
                            weight="regular" 
                            className={activeAnalyticsKey === analytics.key ? 'text-[#4F6660]' : 'text-[#7A9488]'}
                          />
                        <div>
                          <h4 className={`font-medium text-sm ${
                            activeAnalyticsKey === analytics.key ? 'text-[#4F6660]' : 'text-[#2F3A36]'
                          }`}>{analytics.title}</h4>
                          <p className="text-xs text-[#7A7F78]">{analytics.subtitle}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                  {analyticsList.length > 3 && (
                    <div className="text-center pt-2">
                      <span className="text-xs text-gray-400">
                        +{analyticsList.length - 3} meer opties
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Quick Stats Card */}
            <div className="bg-white/85 rounded-2xl shadow-sm border border-[#E8E4DC] p-4 md:bg-white md:rounded-lg md:border-gray-200">
              <div className="flex items-center space-x-3 mb-4">
                <TimeIcon size={18} weight="regular" className="text-[#7A9488]" />
                <div>
                  <h3 className="font-semibold text-[#2F3A36]">Quick Stats</h3>
                  <p className="text-sm text-[#7A7F78]">Snel overzicht</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#2F3A36]">{analyticsData.totalPosts}</p>
                  <p className="text-xs text-[#7A7F78]">Posts</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#2F3A36]">{analyticsData.totalConnections}</p>
                  <p className="text-xs text-[#7A7F78]">Connecties</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#2F3A36]">{analyticsData.totalViews}</p>
                  <p className="text-xs text-[#7A7F78]">Views</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#2F3A36]">{analyticsData.engagementRate.toFixed(1)}%</p>
                  <p className="text-xs text-[#7A7F78]">Engagement</p>
                </div>
              </div>
            </div>


          </div>
        </div>

        {/* Mobile Vibes — filters zoals Inbox, zonder losse navigatiekaarten */}
        <div className="col-span-12 block lg:hidden">
          <div className="px-4 pt-4 pb-24">
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-3" style={{ WebkitOverflowScrolling: 'touch' }}>
              {([
                { key: 'all' as const, label: 'Alles' },
                { key: 'vibescore' as const, label: 'VibeScore' },
                { key: 'overview' as const, label: 'Overzicht' },
                { key: 'time-tracking' as const, label: 'Tijd' },
                { key: 'content' as const, label: 'Content' },
                { key: 'connections' as const, label: 'Connecties' },
                { key: 'engagement' as const, label: 'Engagement' },
                { key: 'entrepreneur-profile' as const, label: 'Profiel' },
              ]).map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setMobileVibesFilter(key);
                    if (key !== 'all' && key !== 'vibescore') {
                      setActiveAnalyticsKey(key);
                      setShowVibeScoreAnalyzer(false);
                    }
                    if (key === 'vibescore') {
                      setShowVibeScoreAnalyzer(true);
                    }
                  }}
                  className={`flex flex-shrink-0 items-center rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                    mobileVibesFilter === key
                      ? 'border-[#8DA8A0] bg-[#F0F7F4] text-[#4F6660]'
                      : 'border-[#DCD8CF] bg-white/55 text-[#6F746F]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {(mobileVibesFilter === 'all' || mobileVibesFilter === 'vibescore') && (
              <button
                type="button"
                onClick={() => {
                  setMobileVibesFilter('vibescore');
                  setShowVibeScoreAnalyzer(true);
                }}
                className="mb-4 flex w-full items-center justify-between border-b border-[#E2DED5] py-3 text-left"
              >
                <div className="flex items-center gap-3">
                  <AnalyticsIcon size={18} weight="regular" className="text-[#7A9488]" />
                  <div>
                    <div className="text-sm font-semibold text-[#2F3A36]">VibeScore Analyzer</div>
                    <div className="text-xs text-[#7A7F78]">Analyseer je vibe</div>
                  </div>
                </div>
                <span className="text-[#8A8A84]">›</span>
              </button>
            )}

            {mobileVibesFilter === 'all' && (
              <div className="mb-5 grid grid-cols-4 gap-2 border-b border-[#E2DED5] pb-4 text-center">
                <div>
                  <div className="text-lg font-bold text-[#2F3A36]">{analyticsData.totalPosts}</div>
                  <div className="text-[10px] text-[#7A7F78]">Posts</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-[#2F3A36]">{analyticsData.totalConnections}</div>
                  <div className="text-[10px] text-[#7A7F78]">Connecties</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-[#2F3A36]">{analyticsData.totalViews}</div>
                  <div className="text-[10px] text-[#7A7F78]">Views</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-[#2F3A36]">{analyticsData.engagementRate.toFixed(1)}%</div>
                  <div className="text-[10px] text-[#7A7F78]">Engagement</div>
                </div>
              </div>
            )}

            {mobileVibesFilter === 'vibescore' && (
              <div className="rounded-2xl border border-[#E2DED5] bg-white/70 p-3">
                <VibeScoreCard />
              </div>
            )}

            {mobileVibesFilter === 'all' ? (
              <div className="space-y-5">
                {renderOverview()}
                {renderContent()}
                {renderConnections()}
                {renderEngagement()}
              </div>
            ) : mobileVibesFilter !== 'vibescore' ? (
              <div>{renderAnalyticsPanel(mobileVibesFilter)}</div>
            ) : null}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="hidden md:block col-span-12 lg:col-span-9 space-y-6">
          {/* Header */}
          <PageHeader
            title="Vibes Analytics"
            subtitle="Inzicht in je community activiteit en engagement"
            icon={<AnalyticsIcon size={24} className="text-gray-500" />}
          />

          {/* VibeScore Analyzer Large Card - Only show when clicked */}
          {showVibeScoreAnalyzer && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">VibeScore Analyzer</h2>
                    <p className="text-sm text-gray-600">Gedetailleerde vibe analyse</p>
                  </div>
                  <button
                    onClick={() => setShowVibeScoreAnalyzer(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="p-6">
                <VibeScoreCard />
              </div>
            </div>
          )}

          {/* Analytics Content - Only show when VibeScore Analyzer is not open */}
          {!showVibeScoreAnalyzer && (
            <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {activeAnalyticsKey === 'overview' ? 'Overzicht' :
                 activeAnalyticsKey === 'time-tracking' ? 'Time & Value Tracking' :
                 activeAnalyticsKey === 'content' ? 'Content Performance' :
                 activeAnalyticsKey === 'connections' ? 'Connectie Groei' :
                 activeAnalyticsKey === 'engagement' ? 'Engagement' :
                 activeAnalyticsKey === 'chakra-demo' ? 'ChakraBar Demo' :
                 activeAnalyticsKey === 'entrepreneur-profile' ? 'Ondernemer Profiel' :
                 activeAnalyticsKey === 'mindtools' ? 'MindTools' :
                 activeAnalyticsKey === 'mindtraining' ? 'MindTraining' :
                 'Analytics'}
              </h2>
              <p className="text-sm text-gray-600">
                {activeAnalyticsKey === 'overview' ? 'Algemene statistieken en trends' :
                 activeAnalyticsKey === 'time-tracking' ? 'Bijhouden waar je tijd aan besteedt en wat het oplevert' :
                 activeAnalyticsKey === 'content' ? 'Posts en bereik analyse' :
                 activeAnalyticsKey === 'connections' ? 'Netwerk ontwikkeling' :
                 activeAnalyticsKey === 'engagement' ? 'Interactie en betrokkenheid' :
                 activeAnalyticsKey === 'chakra-demo' ? 'Test VibeScore visualisatie' :
                 activeAnalyticsKey === 'entrepreneur-profile' ? 'Je persoonlijkheid, werkstijl en skills' :
                 activeAnalyticsKey === 'mindtools' ? 'Mindfulness & wellness tools' :
                 activeAnalyticsKey === 'mindtraining' ? 'Cognitive & mental training' :
                 'Community prestaties'}
              </p>
            </div>
            <div className="p-6">
          {activeAnalyticsKey === 'overview' && renderOverview()}
          {activeAnalyticsKey === 'time-tracking' && userProfile && (
            <TimeTrackingDashboard
              userId={userProfile.uid}
              userProfile={userProfile}
              onRequestCheckOut={() => setShowCheckOutModal(true)}
            />
          )}
          {activeAnalyticsKey === 'content' && renderContent()}
          {activeAnalyticsKey === 'connections' && renderConnections()}
          {activeAnalyticsKey === 'engagement' && renderEngagement()}
              {activeAnalyticsKey === 'chakra-demo' && (
                <div className="h-full">
                  <ChakraBarDemo />
                </div>
              )}
              {activeAnalyticsKey === 'entrepreneur-profile' && renderEntrepreneurProfile()}
              {activeAnalyticsKey === 'mindtools' && renderMindTools()}
              {activeAnalyticsKey === 'mindtraining' && renderMindTraining()}
            </div>
          </div>
          )}
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="hidden md:block">
        <ScrollIndicator />
      </div>

      {/* Check-Out Reflection Modal */}
      {showCheckOutModal && userProfile && (
        <CheckOutReflectionModal
          userId={userProfile.uid}
          userProfile={userProfile}
          sessionDuration={0} // TODO: Calculate actual session duration
          onClose={() => setShowCheckOutModal(false)}
          onComplete={() => {
            setShowCheckOutModal(false);
            // Reload time tracking data
            if (activeAnalyticsKey === 'time-tracking') {
              window.location.reload(); // Simple refresh for now
            }
          }}
        />
      )}
    </div>
  );
};

export default VibesPage;
